import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-sage-200 py-8 text-center text-sm" data-id="element-27">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center gap-4" data-id="element-28">
          <p data-id="element-29">
            © {new Date().getFullYear()} S.O.S. Berrinches. Todos los derechos
            reservados.
          </p>
          <div className="flex gap-6" data-id="element-30">
            <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-31">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-32">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-33">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-34">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-35" />

            {/* Modal */}
            <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} data-id="element-36">
              <div className="flex items-center justify-between px-6 py-4 border-b border-cream-dark" data-id="element-37">
                <h3 className="text-xl font-serif font-bold text-charcoal" data-id="element-38">
                  {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-9 h-9 rounded-full bg-cream flex items-center justify-center text-charcoal-light hover:bg-cream-dark transition-colors" aria-label="Cerrar" data-id="element-39">
                  <XIcon size={18} data-id="element-40" />
                </button>
              </div>

              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-charcoal-light leading-relaxed space-y-4 text-sm" data-id="element-41">
                {activeModal === 'terms' ? <TermsContent data-id="element-42" /> : <PrivacyContent data-id="element-43" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-charcoal-light/70" data-id="element-44">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-45">
        1. Aceptación de los Términos
      </h4>
      <p data-id="element-46">
        Al acceder y utilizar este sitio web y adquirir el producto digital
        "S.O.S. Berrinches", aceptas cumplir con estos Términos y Condiciones.
        Si no estás de acuerdo con alguno de estos términos, te pedimos que no
        utilices nuestros servicios.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-47">
        2. Descripción del Producto
      </h4>
      <p data-id="element-48">
        "S.O.S. Berrinches: El Método Circuit Breaker" es un producto digital
        que incluye una guía en formato PDF, una audio-guía y checklists
        descargables. El contenido es de carácter informativo y educativo, y no
        sustituye el consejo médico o psicológico profesional.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-49">
        3. Licencia de Uso
      </h4>
      <p data-id="element-50">
        Al realizar la compra, se te otorga una licencia personal,
        intransferible y no exclusiva para usar el material. Queda prohibida la
        reproducción, distribución o reventa del contenido sin autorización
        expresa.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-51">
        4. Política de Reembolso
      </h4>
      <p data-id="element-52">
        Debido a la naturaleza digital del producto, las ventas son finales. Sin
        embargo, si no estás satisfecho con tu compra, puedes contactarnos
        dentro de los primeros 7 días para evaluar tu caso.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-53">
        5. Limitación de Responsabilidad
      </h4>
      <p data-id="element-54">
        Los resultados pueden variar según cada familia y situación. No
        garantizamos resultados específicos. El contenido se proporciona "tal
        cual" y no nos hacemos responsables de decisiones tomadas basándose en
        el material.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-55">
        6. Modificaciones
      </h4>
      <p data-id="element-56">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Los cambios serán efectivos desde su publicación en esta
        página.
      </p>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-charcoal-light/70" data-id="element-57">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-58">
        1. Información que Recopilamos
      </h4>
      <p data-id="element-59">
        Recopilamos únicamente la información necesaria para procesar tu compra:
        nombre, dirección de correo electrónico y datos de pago. Los datos de
        pago son procesados de forma segura por nuestro proveedor de pagos y no
        son almacenados en nuestros servidores.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-60">
        2. Uso de la Información
      </h4>
      <p data-id="element-61">
        Tu información personal se utiliza exclusivamente para: procesar y
        entregar tu compra, enviarte el material adquirido y comunicarte
        actualizaciones relevantes sobre el producto (solo si das tu
        consentimiento).
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-62">
        3. Protección de Datos
      </h4>
      <p data-id="element-63">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado, alteración
        o destrucción.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-64">
        4. Cookies
      </h4>
      <p data-id="element-65">
        Este sitio puede utilizar cookies esenciales para su funcionamiento. No
        utilizamos cookies de seguimiento publicitario ni compartimos tu
        información con terceros con fines de marketing.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-66">
        5. Tus Derechos
      </h4>
      <p data-id="element-67">
        Tienes derecho a acceder, rectificar o eliminar tus datos personales en
        cualquier momento. También puedes solicitar la limitación del
        tratamiento de tus datos o la portabilidad de los mismos.
      </p>

      <h4 className="font-serif font-bold text-charcoal text-base" data-id="element-68">
        6. Cambios en esta Política
      </h4>
      <p data-id="element-69">
        Nos reservamos el derecho de actualizar esta política de privacidad.
        Cualquier cambio será publicado en esta página con la fecha de
        actualización correspondiente.
      </p>
    </>;
}